import type { SocialAuthProvider } from "@pax/api-contract/client-config";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@pax/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { createContext, type ReactNode, use, useEffect, useRef, useState } from "react";
import { UNREACHABLE_MESSAGE } from "@/lib/api";
import { authClient } from "@/lib/auth-client";
import { broadcastAccountChange } from "./auth-changes";
import { guestUpgradeUrls, signInErrorCallback } from "./auth-urls";
import { GuestOption } from "./components/GuestOption";
import { SocialAuthButtons } from "./components/SocialAuthButtons";
import { startGuestSession } from "./guest-session";
import { clientConfigQuery, type Session, sessionKey, useSession } from "./queries";
import { signInErrorMessage } from "./sign-in-errors";
import { linkProvider, signInWithProvider } from "./social-sign-in";

/** Something a signed-out reader or a guest tried that only an account can do. */
export type AccountAction =
  | "comment"
  | "vote"
  | "react"
  | "save"
  | "copy_collection"
  | "make_assets"
  | "make_collections"
  | "report";

/** Something a signed-out reader tried that a guest can do too, so playing as one is offered. */
type SessionAction = "start_game";

/** Why the dialog opened: something tried that needs a session or an account, or a guest's credits ran out. */
export type AccountDialogReason = "out_of_credit" | AccountAction | SessionAction;

interface AccountDialogContextValue {
  /**
   * Asks for an account, titled with why. Whoever is signed out signs in, or plays as a guest when
   * the reason allows one; a guest creates an account by connecting a provider to the guest they
   * are. Either way they go on to `next`, or come back to this page when it is left out.
   */
  openAccountDialog: (reason?: AccountDialogReason, options?: { next?: string }) => void;
}

/**
 * What each reason is for, said after "Sign in to" for a signed-out reader or "Create an account
 * to" for a guest, and why it is worth it. `benefits: false` leaves out the perks, where selling
 * an account would be out of place, and `guests: true` offers playing as a guest instead.
 */
const REASON_COPY: Record<
  AccountDialogReason,
  { purpose: string; description: string; benefits?: false; guests?: true }
> = {
  comment: {
    purpose: "comment",
    description:
      "Give feedback to preset creators and engage in discussions. An account also unlocks:",
  },
  vote: {
    purpose: "vote on comments",
    description:
      "Upvote the comments you find helpful so others see them first. An account also unlocks:",
  },
  react: {
    purpose: "react to assets",
    description: "Let creators know which assets you like. An account also unlocks:",
  },
  save: {
    purpose: "save assets",
    description:
      "Save the assets that you like and find them again anytime. An account also unlocks:",
  },
  copy_collection: {
    purpose: "copy collections",
    description:
      "Make your own copy of a collection and change it however you want. An account also unlocks:",
  },
  make_assets: {
    purpose: "make assets",
    description:
      "Upload images, video, audio, and maps that you can use in your games and share with the community. An account also unlocks:",
  },
  make_collections: {
    purpose: "make collections",
    description:
      "Gather the assets you like into collections and share them with others. An account also unlocks:",
  },
  report: {
    purpose: "report",
    description: "Reporting needs an account. Creating one takes a moment.",
    benefits: false,
  },
  start_game: {
    purpose: "start this game",
    description: "Pick up your games on any device.",
    benefits: false,
    guests: true,
  },
  out_of_credit: {
    purpose: "keep playing",
    description:
      "Your guest credits have run out, so the game master can't take your turn. Create an account to continue and unlock:",
  },
};

/** Signing in, or a guest creating an account: worded for which, and for what they tried. */
type DialogMode = "signIn" | "upgrade";

/** The dialog opened for no particular reason, such as from a guest's Save progress. */
const NO_REASON: Record<DialogMode, { title: string; description: string }> = {
  signIn: { title: "Sign in to Pax", description: "An account unlocks:" },
  upgrade: {
    title: "Create your account",
    description: "You're playing as a guest. Create an account to unlock:",
  },
};

function dialogCopy(
  mode: DialogMode,
  reason: AccountDialogReason | null,
): { title: string; description: string; benefits: boolean; guests: boolean } {
  if (!reason) return { ...NO_REASON[mode], benefits: true, guests: false };
  const { purpose, description, benefits, guests } = REASON_COPY[reason];
  const verb = mode === "signIn" ? "Sign in" : "Create an account";
  return {
    title: `${verb} to ${purpose}`,
    description,
    benefits: benefits !== false,
    guests: mode === "signIn" && guests === true,
  };
}

const AccountDialogContext = createContext<AccountDialogContextValue | null>(null);
const ACCOUNT_BENEFITS = [
  "1 bonus token right away",
  "Daily check-in bonus tokens",
  "Progress saved across all your devices",
  "A place on the leaderboards",
] as const;

export function useAccountDialog(): AccountDialogContextValue {
  const value = use(AccountDialogContext);
  if (!value) throw new Error("useAccountDialog must be used inside AccountDialogProvider");
  return value;
}

/**
 * The account dialog, for anything someone tries that needs an account while they stay on the
 * page: a guest connects a provider to the guest they are, and a signed-out reader signs in and
 * comes back. Pages that need an account send people to `/login` or `/create-account` instead.
 */
export function AccountDialogProvider({
  children,
  result,
  resultErrorCode,
  onResultDismissed,
}: {
  children: ReactNode;
  result?: "success" | "failed";
  resultErrorCode?: string;
  onResultDismissed: () => void;
}) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const completionStarted = useRef(false);
  const [requested, setRequested] = useState(false);
  const [reason, setReason] = useState<AccountDialogReason | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [guestPending, setGuestPending] = useState(false);
  const { data: session } = useSession();
  // Whoever has no session signs in; anyone else here is a guest, since accounts aren't asked.
  const mode: DialogMode = session === null ? "signIn" : "upgrade";
  const [dismissedResultCode, setDismissedResultCode] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const resultOpen =
    result === "failed" && resultErrorCode !== undefined && resultErrorCode !== dismissedResultCode;
  const open = requested || resultOpen;
  const methods = useQuery({ ...clientConfigQuery, enabled: open });

  useEffect(() => {
    if (result !== "success" || completionStarted.current) return;
    completionStarted.current = true;

    void authClient
      .getSession()
      .then(({ data, error }) => {
        if (error || !data) {
          completionStarted.current = false;
          setRequested(true);
          setLocalError(error?.message?.trim() || UNREACHABLE_MESSAGE);
          return;
        }

        queryClient.setQueryData<Session>(sessionKey, data);
        broadcastAccountChange(data.user.id);
        void router.invalidate();
        onResultDismissed();
      })
      .catch(caught => {
        completionStarted.current = false;
        setRequested(true);
        setLocalError(caught instanceof Error ? caught.message : UNREACHABLE_MESSAGE);
      });
  }, [onResultDismissed, queryClient, result, router]);

  function close() {
    setRequested(false);
    setReason(null);
    setNext(null);
    setGuestPending(false);
    setLocalError(null);
    if (result) {
      if (resultErrorCode) {
        setDismissedResultCode(resultErrorCode);
      }
      onResultDismissed();
    }
  }

  const copy = dialogCopy(mode, reason);
  const destination = next ?? router.state.location.href;

  async function continueWith(provider: SocialAuthProvider) {
    setLocalError(null);
    try {
      if (mode === "signIn") {
        await signInWithProvider({
          provider,
          callbackURL: destination,
          errorCallbackURL: signInErrorCallback(destination, copy.guests ? undefined : "account"),
        });
      } else {
        await linkProvider({ provider, ...guestUpgradeUrls() });
      }
    } catch (caught) {
      setLocalError(caught instanceof Error ? caught.message : UNREACHABLE_MESSAGE);
    }
  }

  async function playAsGuest() {
    setGuestPending(true);
    setLocalError(null);
    try {
      await startGuestSession(queryClient);
      close();
      await router.navigate({ href: destination });
    } catch (caught) {
      setLocalError(caught instanceof Error ? caught.message : UNREACHABLE_MESSAGE);
      setGuestPending(false);
    }
  }

  const errorMessage =
    localError ??
    signInErrorMessage(resultOpen ? resultErrorCode : undefined) ??
    (methods.isError ? UNREACHABLE_MESSAGE : null);

  return (
    <AccountDialogContext
      value={{
        openAccountDialog: (nextReason, options) => {
          setReason(nextReason ?? null);
          setNext(options?.next ?? null);
          setRequested(true);
        },
      }}
    >
      {children}
      <Dialog
        open={open}
        onOpenChange={nextOpen => {
          if (!nextOpen) close();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{copy.title}</DialogTitle>
            <DialogDescription>{copy.description}</DialogDescription>
          </DialogHeader>

          {copy.benefits && (
            <ul className="space-y-2 text-sm">
              {ACCOUNT_BENEFITS.map(benefit => (
                <li key={benefit} className="flex items-center gap-2">
                  <Check className="size-4 text-primary" aria-hidden />
                  {benefit}
                </li>
              ))}
            </ul>
          )}
          {mode === "upgrade" && (
            <p className="text-sm text-muted-foreground">
              Your current guest progress will be saved to the new account.
            </p>
          )}

          {errorMessage ? (
            <p role="alert" className="rounded-lg bg-destructive/10 px-3 py-2 text-destructive">
              {errorMessage}
            </p>
          ) : null}

          {methods.data ? (
            <SocialAuthButtons
              providers={methods.data.socialProviders}
              onSelect={provider => void continueWith(provider)}
            />
          ) : methods.isPending ? (
            <div className="h-20 animate-pulse rounded-lg bg-muted" role="status">
              <span className="sr-only">Loading sign-in methods</span>
            </div>
          ) : null}

          {copy.guests && (
            <GuestOption pending={guestPending} onPlay={() => void playAsGuest()} className="" />
          )}
        </DialogContent>
      </Dialog>
    </AccountDialogContext>
  );
}
